import React from "react";
import { ColumnDef } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { MessageSquareReply } from "lucide-react";
import { Ticket, TicketStatus } from "../types";
import { formatDateTimeGMT5 } from "@/utils/timezone";

const statusClass: Record<TicketStatus, string> = {
  Pending: "bg-yellow-100 text-yellow-700",
  "In Progress": "bg-blue-100 text-blue-700",
  Resolved: "bg-green-100 text-green-700",
  Closed: "bg-gray-100 text-gray-600",
};

export interface TicketColumnOptions {
  onReply: (ticket: Ticket) => void;
  userMap?: Map<number, string>;
}

export function createTicketColumns(options: TicketColumnOptions): ColumnDef<Ticket>[] {
  return [
    { accessorKey: "id", header: "ID" },
    {
      accessorKey: "user_id",
      header: "User",
      cell: ({ row }) => {
        const userId = row.getValue("user_id") as number;
        const username = options.userMap?.get(userId);
        return username ? (
          <span className="font-medium">{username}</span>
        ) : (
          <span className="text-muted-foreground text-sm">#{userId}</span>
        );
      },
    },
    {
      accessorKey: "subject",
      header: "Subject",
      cell: ({ row }) => (
        <span className="font-medium">{row.getValue("subject") as string}</span>
      ),
    },
    {
      accessorKey: "description",
      header: "Description",
      cell: ({ row }) => {
        const desc = row.getValue("description") as string;
        if (!desc) return <span className="text-muted-foreground">—</span>;
        return <span className="line-clamp-2 max-w-xs text-sm text-muted-foreground">{desc}</span>;
      },
    },
    {
      accessorKey: "file_url",
      header: "Attachment",
      cell: ({ row }) => {
        const url = row.getValue("file_url") as string | null;
        if (!url) return <span className="text-muted-foreground">—</span>;
        return (
          <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs">
            View File
          </a>
        );
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => {
        const status = row.getValue("status") as TicketStatus;
        const cls = statusClass[status] ?? "bg-gray-100 text-gray-600";
        return (
          <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
            {status}
          </span>
        );
      },
    },
    {
      accessorKey: "created_at",
      header: "Created",
      cell: ({ row }) => {
        const date = row.getValue("created_at") as string | undefined;
        if (!date) return <span className="text-muted-foreground">—</span>;
        return <span className="text-sm">{formatDateTimeGMT5(new Date(date))}</span>;
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <Button
          size="sm"
          variant="outline"
          className="flex items-center gap-1"
          onClick={() => options.onReply(row.original)}
        >
          <MessageSquareReply size={14} />
          Reply
        </Button>
      ),
    },
  ];
}
